import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { InitiativeWithStats } from '../../hooks/useInitiativeData';
import Button from '../ui/Button';

interface InitiativeCardProps {
  initiative: InitiativeWithStats;
}

const Card = styled.article`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  background: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  border: 1px solid ${props => props.theme.colors.border};
  overflow: hidden;
  transition:
    border-color ${props => props.theme.animation.duration.normal} ${props => props.theme.animation.easing.easeOut},
    box-shadow ${props => props.theme.animation.duration.normal} ${props => props.theme.animation.easing.easeOut};

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 420px 1fr;
  }

  &:hover {
    border-color: ${props => props.theme.colors.neutral[300]};
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

const ImageSection = styled.div<{ imageUrl: string }>`
  width: 100%;
  height: 280px;
  background-image: url(${props => props.imageUrl});
  background-size: cover;
  background-position: center;

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    height: 100%;
    min-height: 380px;
  }
`;

const ContentSection = styled.div`
  padding: ${props => props.theme.spacing[8]};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[6]};

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    padding: ${props => props.theme.spacing[10]};
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: ${props => props.theme.typography.letterSpacing.eyebrow};
  color: ${props => props.theme.colors.textMuted};
  width: fit-content;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: ${props => props.theme.borderRadius.full};
    background: ${props => props.theme.colors.primary[500]};
  }
`;

const Title = styled.h2`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  color: ${props => props.theme.colors.ink};
  line-height: ${props => props.theme.typography.lineHeight.tight};
  margin: 0;
  max-width: 20ch;
`;

const Description = styled.p`
  font-size: ${props => props.theme.typography.fontSize.base};
  color: ${props => props.theme.colors.textMuted};
  line-height: ${props => props.theme.typography.lineHeight.relaxed};
  margin: 0;
  max-width: 56ch;
`;

/** Figures sit on a hairline rule rather than inside a nested grey box. */
const StatsGrid = styled.dl`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing[10]};
  margin: 0;
  padding-top: ${props => props.theme.spacing[6]};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
`;

const StatLabel = styled.dt`
  font-size: ${props => props.theme.typography.fontSize.xs};
  text-transform: uppercase;
  letter-spacing: ${props => props.theme.typography.letterSpacing.eyebrow};
  color: ${props => props.theme.colors.textMuted};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  margin: 0;
`;

const StatValue = styled.dd`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  letter-spacing: ${props => props.theme.typography.letterSpacing.display};
  color: ${props => props.theme.colors.primary[500]};
  margin: 0;
  line-height: 1;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing[3]};
  margin-top: auto;
`;

const InitiativeCard: React.FC<InitiativeCardProps> = ({ initiative }) => {
  const { stats } = initiative;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Card>
      <ImageSection imageUrl={initiative.imageUrl} />
      <ContentSection>
        <div>
          <Badge>
            {initiative.status === 'current'
              ? 'Active Campaign'
              : initiative.status === 'past'
              ? 'Completed Campaign'
              : 'Upcoming Campaign'}
          </Badge>
        </div>

        <div>
          <Title>{initiative.title}</Title>
          <Description style={{ marginTop: '1rem' }}>{initiative.shortDescription}</Description>
        </div>

        <StatsGrid>
          <StatItem>
            <StatLabel>Goal</StatLabel>
            <StatValue>{formatCurrency(stats.goalAmount)}</StatValue>
          </StatItem>
          {initiative.status === 'past' && stats.raisedAmount > 0 && (
            <StatItem>
              <StatLabel>Raised</StatLabel>
              <StatValue>{formatCurrency(stats.raisedAmount)}</StatValue>
            </StatItem>
          )}
        </StatsGrid>

        <ButtonGroup>
          <Button
            as={Link}
            to={`/initiatives/${initiative.slug}`}
            variant="primary"
            icon={<ArrowRight />}
          >
            More information
          </Button>
        </ButtonGroup>
      </ContentSection>
    </Card>
  );
};

export default InitiativeCard;
